import { createTheme, alpha, Theme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypeText {
    muted: string;
    hint: string;
    highlight: string;
  }

  interface RoleColors {
    superadmin: string;
    customeradmin: string;
    clientadmin: string;
    user: string;
    sdkuser: string;
    [key: string]: string; // optional fallback for dynamic roles
  }

  interface MedallionColors {
    gold: string;
    silver: string;
    bronze: string;
  }

  // add strongly-typed codeBlock config to theme
  interface Theme {
    roleColors: RoleColors;
    medallionColors: MedallionColors;
    chartPalette: string[];

    codeBlock: {
      background: string;
      color: string;
      fontFamily: string;
      padding: string;
      borderRadius: number;
      whiteSpace: string;
      lineHeight: number;
      fontSize: number;
      fontWeight?: string | number;
    };
  }
  interface ThemeOptions {
    roleColors?: Partial<RoleColors>;
    medallionColors?: Partial<MedallionColors>;
    chartPalette?: string[];
    codeBlock?: Partial<Theme['codeBlock']>;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  components: {
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: '1px solid #E5E7EB',
          // borderRadius: '4px',
          // backgroundColor: theme.palette.grey[100],
          minHeight: '40px',
          // '& .MuiTabs-indicator': {
          //   display: 'none', // remove underline indicator
          // },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: '40px',
          padding: '6px 16px',

          '&:hover': {
            backgroundColor: '#F3F4F6',
            color: theme.palette.primary.main,
          },

          '&.Mui-selected': {
            fontWeight: 600,
          },
        }),
      },
    },

    MuiButton: {
      styleOverrides: {
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        *::-webkit-scrollbar {
          width: 0.4em;
          height: 0.6em;
          background: transparent;
        }

        *::-webkit-scrollbar-thumb {
          background-color: transparent;
          border-radius: 8px;
          transition: background-color 0.3s ease;
        }

        *:hover::-webkit-scrollbar-thumb {
          background-color:rgba(105, 94, 168, 0.23);
        }

        *::-webkit-scrollbar-thumb:hover {
          background-color: rgba(105, 94, 168, 0.4);
        }

        *::-webkit-scrollbar-track {
          background: transparent;
        }
      `,
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#9B93D4',
          fontSize: '12px',
        },
        arrow: {
          color: '#9B93D4',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#695ea8',
      // highlight: '#9B93D4',
      // highlight: '#9B93D4',
    },
    error: {
      main: '#bf0b0b',
      // dark: '#d32f2f',
      // light: '#f6a9a9',
    },
    text: {
      primary: '#475569',
      secondary: '#313131',
      disabled: '#94A3B8',
      hint: '#94A3B8', //Need to add meaningful color
      muted: '#CBD5E1', //Need to add meaningful color
      highlight: '#374151', // Add your slightly darker grey here
    },
  },

  codeBlock: {
    background: '#f5f5f5', // light grey
    color: '#3d3a3a', // very dark grey
    fontFamily: 'monospace',
    padding: '18px',
    borderRadius: 8,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
    fontSize: 13,
    fontWeight: 'bold',
  },
  roleColors: {
    superadmin: '#c62828', // red for admins
    customeradmin: '#7b1fa2', // purple for managers
    clientadmin: '#2e7d32', // green for normal users
    user: '#ef6c00', // neutral grey for guests
    sdkuser: '#1976d2', // blue for SDK users
  },
  medallionColors: {
    gold: '#EAB308',
    silver: '#9CA3AF',
    bronze: '#F97316',
  },
  chartPalette: ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#DB2777', '#65A30D'],
} as ThemeOptions);

export default theme;
